/**
 * Step-based Permission Wizard for Proctoring SDK
 * Modular design allowing easy addition of new steps
 */

// Lucide Icons (inline SVGs)
const icons = {
    camera: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13.997 4a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 1.759-1.048l.489-.904A2 2 0 0 1 10.004 4z"/><circle cx="12" cy="13" r="3"/></svg>`,
    mic: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19v3"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><rect x="9" y="2" width="6" height="13" rx="3"/></svg>`,
    screen: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/></svg>`,
    screenShare: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 3H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-3"/><path d="M8 21h8"/><path d="M12 17v4"/><path d="m17 8 5-5"/><path d="M17 3h5v5"/></svg>`,
    user: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`,
    check: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>`,
    shield: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/></svg>`,
    loader: `<svg class="spin" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>`,
    error: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>`,
    alert: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/></svg>`,
    monitor: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/><path d="m9 10 2 2 4-4"/></svg>`,
};

// CSS Styles
const styles = `
    * { box-sizing: border-box; margin: 0; padding: 0; }

    :host {
        --wizard-primary: #3b82f6;
        --wizard-primary-dark: #2563eb;
        --wizard-success: #10b981;
        --wizard-error: #ef4444;
        --wizard-bg: #f8fafc;
        --wizard-card: #ffffff;
        --wizard-text: #1e293b;
        --wizard-text-muted: #64748b;
        --wizard-border: #e2e8f0;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        line-height: 1.5;
    }

    /* Dark Theme */
    .wizard-overlay.dark {
        --wizard-bg: #0f172a;
        --wizard-card: #1e293b;
        --wizard-text: #f1f5f9;
        --wizard-text-muted: #94a3b8;
        --wizard-border: #334155;
    }

    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }

    .spin { animation: spin 1s linear infinite; }

    .wizard-overlay {
        position: fixed;
        inset: 0;
        z-index: 999999;
        background: var(--wizard-bg);
        display: flex;
        pointer-events: auto;
    }

    /* Sidebar */
    .wizard-sidebar {
        width: 280px;
        background: var(--wizard-card);
        border-right: 1px solid var(--wizard-border);
        display: flex;
        flex-direction: column;
        padding: 24px;
    }

    .wizard-logo {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 32px;
    }

    .wizard-logo-icon {
        width: 40px;
        height: 40px;
        background: var(--wizard-primary);
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
    }

    .wizard-logo-icon svg { width: 24px; height: 24px; }

    .wizard-logo-text h2 {
        font-size: 16px;
        font-weight: 600;
        color: var(--wizard-text);
    }

    .wizard-logo-text p {
        font-size: 12px;
        color: var(--wizard-text-muted);
    }

    /* Step List */
    .step-list { flex: 1; }

    .step-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        border-radius: 8px;
        margin-bottom: 8px;
        transition: all 0.2s;
        cursor: default;
    }

    .step-item.active {
        background: rgba(59, 130, 246, 0.1);
        border-left: 3px solid var(--wizard-primary);
    }

    .step-item.completed .step-icon {
        background: var(--wizard-success);
        color: white;
    }

    .step-item.active .step-icon {
        background: var(--wizard-primary);
        color: white;
    }

    .step-icon {
        width: 32px;
        height: 32px;
        border-radius: 8px;
        background: var(--wizard-border);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .step-icon svg { width: 18px; height: 18px; }

    .step-info h4 {
        font-size: 14px;
        font-weight: 500;
        color: var(--wizard-text);
    }

    .step-info p {
        font-size: 12px;
        color: var(--wizard-text-muted);
    }

    .step-status {
        margin-left: auto;
    }

    .step-status svg {
        width: 20px;
        height: 20px;
    }

    .step-status .check { color: var(--wizard-success); }
    .step-status .loading { color: var(--wizard-primary); }

    /* Main Content */
    .wizard-main {
        flex: 1;
        display: flex;
        flex-direction: column;
        padding: 32px;
        overflow-y: auto;
    }

    .wizard-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        animation: fadeIn 0.3s ease-out;
    }

    .content-icon {
        width: 80px;
        height: 80px;
        background: rgba(59, 130, 246, 0.1);
        border-radius: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 24px;
        color: var(--wizard-primary);
    }

    .content-icon svg { width: 40px; height: 40px; }
    .content-icon.success { background: rgba(16, 185, 129, 0.1); color: var(--wizard-success); }
    .content-icon.error { background: rgba(239, 68, 68, 0.1); color: var(--wizard-error); }

    .content-title {
        font-size: 24px;
        font-weight: 600;
        color: var(--wizard-text);
        margin-bottom: 8px;
    }

    .content-description {
        font-size: 14px;
        color: var(--wizard-text-muted);
        max-width: 400px;
        margin-bottom: 24px;
    }

    /* Camera Preview */
    .camera-setup {
        display: flex;
        gap: 24px;
        align-items: flex-start;
        width: 100%;
        max-width: 700px;
    }

    .camera-preview-container {
        flex: 1;
        min-width: 320px;
        border-radius: 12px;
        overflow: hidden;
        background: #000;
    }

    .camera-preview-container video {
        width: 100%;
        aspect-ratio: 16/9;
        object-fit: cover;
        display: block;
    }

    /* Device Selectors */
    .device-selectors {
        flex: 0 0 220px;
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .device-select-group {
        text-align: left;
    }

    .device-select-label {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        font-weight: 500;
        color: var(--wizard-text);
        margin-bottom: 8px;
    }

    .device-select-label svg {
        width: 16px;
        height: 16px;
        color: var(--wizard-text-muted);
    }

    .device-select {
        width: 100%;
        padding: 10px 12px;
        font-size: 14px;
        border: 1px solid var(--wizard-border);
        border-radius: 8px;
        background: var(--wizard-card);
        color: var(--wizard-text);
        cursor: pointer;
        transition: border-color 0.2s;
    }

    .device-select:focus {
        outline: none;
        border-color: var(--wizard-primary);
    }

    .device-select option {
        background: var(--wizard-card);
        color: var(--wizard-text);
    }

    /* Responsive - stack on small screens */
    @media (max-width: 700px) {
        .camera-setup {
            flex-direction: column;
        }
        .camera-preview-container {
            min-width: 100%;
        }
        .device-selectors {
            flex: none;
            width: 100%;
        }
    }

    .permission-pending {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        padding: 32px;
        background: var(--wizard-card);
        border: 1px solid var(--wizard-border);
        border-radius: 12px;
        max-width: 400px;
    }

    .permission-pending svg {
        width: 48px;
        height: 48px;
        color: var(--wizard-primary);
    }

    .permission-pending p {
        color: var(--wizard-text-muted);
        text-align: center;
    }

    .permission-pending .btn {
        margin-top: 16px;
    }

    .permission-pending .btn svg {
        width: 18px;
        height: 18px;
    }

    /* Error Message */
    .error-message {
        background: rgba(239, 68, 68, 0.1);
        border: 1px solid rgba(239, 68, 68, 0.3);
        border-radius: 8px;
        padding: 12px 16px;
        color: var(--wizard-error);
        font-size: 13px;
        margin-bottom: 16px;
        max-width: 400px;
    }

    /* Footer */
    .wizard-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-top: 24px;
        border-top: 1px solid var(--wizard-border);
    }

    .progress-info {
        font-size: 13px;
        color: var(--wizard-text-muted);
    }

    .wizard-buttons {
        display: flex;
        gap: 12px;
    }

    .btn {
        padding: 10px 24px;
        font-size: 14px;
        font-weight: 500;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .btn-secondary {
        background: transparent;
        border: 1px solid var(--wizard-border);
        color: var(--wizard-text);
    }

    .btn-secondary:hover:not(:disabled) {
        background: var(--wizard-bg);
    }

    .btn-primary {
        background: var(--wizard-primary);
        border: none;
        color: white;
    }

    .btn-primary:hover:not(:disabled) {
        background: var(--wizard-primary-dark);
    }

    /* User Info Panel */
    .user-panel {
        width: 280px;
        background: var(--wizard-card);
        border-left: 1px solid var(--wizard-border);
        padding: 24px;
        display: flex;
        flex-direction: column;
    }

    .user-card {
        background: var(--wizard-bg);
        border-radius: 12px;
        padding: 24px;
        text-align: center;
    }

  
    .user-avatar {
        width: 64px;
        height: 64px;
        background: var(--wizard-primary);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 16px;
        color: white;
        font-size: 24px;
        font-weight: 600;
    }

    .user-name {
        font-size: 18px;
        font-weight: 600;
        color: var(--wizard-text);
        margin-bottom: 4px;
    }

    .user-email {
        font-size: 13px;
        color: var(--wizard-text-muted);
        margin-bottom: 16px;
    }

    .user-details {
        border-top: 1px solid var(--wizard-border);
        padding-top: 16px;
    }

    .user-detail-item {
        display: flex;
        justify-content: space-between;
        padding: 8px 0;
        font-size: 13px;
    }

    .user-detail-item .label {
        color: var(--wizard-text-muted);
    }

    .user-detail-item .value {
        color: var(--wizard-text);
        font-weight: 500;
    }

    /* Progress Bar */
    .progress-bar-container {
        margin-top: auto;
        padding-top: 24px;
    }

    .progress-bar-label {
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        color: var(--wizard-text-muted);
        margin-bottom: 8px;
    }

    .progress-bar {
        height: 6px;
        background: var(--wizard-border);
        border-radius: 3px;
        overflow: hidden;
    }

    .progress-bar-fill {
        height: 100%;
        background: var(--wizard-primary);
        border-radius: 3px;
        transition: width 0.3s ease;
    }

    /* Mobile Responsive */
    @media (max-width: 900px) {
        .wizard-sidebar, .user-panel { display: none; }
        .wizard-main { padding: 16px; }
    }

    /* Browser Check Checklist - Compact */
    .check-list {
        width: 100%;
        max-width: 480px;
        background: var(--wizard-card);
        border: 1px solid var(--wizard-border);
        border-radius: 10px;
        padding: 12px 16px;
        text-align: left;
    }

    .check-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 10px;
        border-radius: 6px;
        margin-bottom: 4px;
        transition: background 0.2s;
    }

    .check-item:last-child {
        margin-bottom: 0;
    }

    .check-item.checking {
        background: rgba(59, 130, 246, 0.05);
    }

    .check-item.pass {
        background: rgba(16, 185, 129, 0.05);
    }

    .check-item.fail {
        background: rgba(239, 68, 68, 0.05);
    }

    .check-icon {
        width: 18px;
        height: 18px;
        flex-shrink: 0;
    }

    .check-icon svg {
        width: 18px;
        height: 18px;
    }

    .check-icon.checking {
        color: var(--wizard-primary);
    }

    .check-icon.pass {
        color: var(--wizard-success);
    }

    .check-icon.fail {
        color: var(--wizard-error);
    }

    .check-content {
        flex: 1;
        min-width: 0;
    }

    .check-title {
        font-size: 13px;
        font-weight: 500;
        color: var(--wizard-text);
    }

    .check-description {
        display: none;
    }

    .check-error {
        font-size: 11px;
        color: var(--wizard-error);
        margin-top: 2px;
        line-height: 1.3;
    }

    .recommended-browsers {
        margin-top: 12px;
        padding-top: 12px;
        border-top: 1px solid var(--wizard-border);
    }

    .recommended-browsers h4 {
        font-size: 12px;
        font-weight: 500;
        color: var(--wizard-text);
        margin-bottom: 6px;
    }

    .browser-list {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }

    .browser-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        background: var(--wizard-bg);
        border: 1px solid var(--wizard-border);
        border-radius: 4px;
        font-size: 11px;
        color: var(--wizard-text-muted);
    }

    .browser-badge.recommended {
        border-color: var(--wizard-success);
        color: var(--wizard-success);
    }

    /* Instructions Step */
    .instructions-list {
        width: 100%;
        max-width: 600px;
        margin-top: 16px;
        background: var(--wizard-card);
        border: 1px solid var(--wizard-border);
        border-radius: 12px;
        overflow: hidden;
    }

    .instruction-item {
        border-bottom: 1px solid var(--wizard-border);
        transition: background-color 0.2s;
    }
         .instruction-item:hover {
        background-color: var(--wizard-bg);
    }

    .instruction-item:last-child {
        border-bottom: none;
    }

    .instruction-header {
        display: flex;
        align-items: center;
        padding: 16px;
        gap: 12px;
        cursor: pointer;
        width: 100%;
        text-align: left;
        background: none;
        border: none;
        color: inherit;
    }

 

    .instruction-icon {
        color: var(--wizard-primary);
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .instruction-icon svg {
        width: 20px;
        height: 20px;
    }

    .instruction-content {
        flex: 1;
    }

    .instruction-content h5 {
        font-size: 15px;
        font-weight: 600;
        color: var(--wizard-text);
        margin-bottom: 2px;
    }

    .instruction-content p {
        font-size: 13px;
        color: var(--wizard-text-muted);
        line-height: 1.4;
    }

    .instruction-toggle {
        color: var(--wizard-text-muted);
        transition: transform 0.2s;
    }

    .instruction-item.expanded .instruction-toggle {
        transform: rotate(180deg);
    }

    .instruction-details {
        padding: 0 16px 16px 48px;
        font-size: 13px;
        color: var(--wizard-text-muted);
        line-height: 1.5;
        display: none;
        animation: fadeIn 0.2s ease-out;
    }


    .instruction-item.expanded .instruction-details {
        display: block;
    }

    .acknowledgement-box {
        margin-top: 24px;
        display: flex;
        align-items: center;
        gap: 12px;
        background: rgba(59, 130, 246, 0.05);
        padding: 16px;
        border-radius: 8px;
        border: 1px solid rgba(59, 130, 246, 0.2);
        width: 100%;
        max-width: 600px;
        cursor: pointer;
        transition: all 0.2s;
    }

    .acknowledgement-box:hover {
        background: rgba(59, 130, 246, 0.1);
        border-color: rgba(59, 130, 246, 0.4);
    }

    .acknowledgement-box input[type="checkbox"] {
        width: 18px;
        height: 18px;
        cursor: pointer;
        accent-color: var(--wizard-primary);
    }

    .acknowledgement-box label {
        font-size: 14px;
        font-weight: 500;
        color: var(--wizard-text);
        cursor: pointer;
        user-select: none;
        flex: 1;
    }
`;

class PermissionWizard {
    overlay = null;
    shadowRoot = null;
    currentStep = 0;
    isExecuting = false;
    streams = {
        camera: null,
        screen: null,
    };

    // Default steps - can be extended
    steps = [
        {
            id: "user-info",
            title: "Verify Your Details",
            description: "Confirm your identity before proceeding",
            icon: "user",
            action: null, // No action needed, just display
        },
        {
            id: "browser-check",
            title: "System Compatibility",
            description: "Checking your browser and device",
            icon: "monitor",
            action: "checkBrowser",
        },
        {
            id: "camera",
            title: "Camera Access",
            description: "Allow camera access to verify your identity",
            icon: "camera",
            action: "requestCamera",
        },
        {
            id: "screen",
            title: "Screen Share",
            description: "Share your screen to ensure exam integrity",
            icon: "screen",
            action: "requestScreen",
        },
        {
            id: "instructions",
            title: "Proctoring Guidelines",
            description: "Review important rules before starting",
            icon: "shield",
            action: "showInstructions",
        },
    ];

    // User details - dummy data by default
    user_details = {
        name: "John Doe",
        email: "john.doe@example.com",
        attempt_id: "ATT-2024-001234",
        exam_name: "Proctored Assessment",
    };

    // Theme - 'light' or 'dark'
    theme = "dark";

    callbacks = {
        onComplete: null,
        onCancel: null,
        onStepComplete: null,
    };

    init(shadowRoot) {
        this.shadowRoot = shadowRoot;
    }

    /**
     * Start the permission wizard
     * @param {Object} options - { user_details, steps, callbacks, theme }
     */
    start(options = {}) {
        if (options.user_details) {
            this.user_details = {
                ...this.user_details,
                ...options.user_details,
            };
        }
        if (options.steps) {
            this.steps = options.steps;
        }
        if (options.callbacks) {
            this.callbacks = { ...this.callbacks, ...options.callbacks };
        }
        if (options.theme) {
            this.theme = options.theme;
        }

        this.currentStep = 0;
        this.createUI();
        this.executeCurrentStep();
    }

    createUI() {
        // Remove existing overlay
        if (this.overlay) {
            this.overlay.remove();
        }

        // Inject styles
        let styleEl = this.shadowRoot.querySelector("#wizard-styles");
        if (!styleEl) {
            styleEl = document.createElement("style");
            styleEl.id = "wizard-styles";
            styleEl.textContent = styles;
            this.shadowRoot.appendChild(styleEl);
        }

        // Create overlay
        this.overlay = document.createElement("div");
        this.overlay.className = `wizard-overlay ${this.theme}`;

        const initials = this.user_details.name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .substring(0, 2)
            .toUpperCase();

        this.overlay.innerHTML = `
            <!-- Sidebar -->
            <div class="wizard-sidebar">
                <div class="wizard-logo">
                    <div class="wizard-logo-icon">${icons.shield}</div>
                    <div class="wizard-logo-text">
                        <h2>Secure Setup</h2>
                        <p>Exam Environment Check</p>
                    </div>
                </div>
                <div class="step-list">
                    ${this.steps
                .map(
                    (step, i) => `
                        <div class="step-item" data-step="${i}">
                            <div class="step-icon">${icons[step.icon] || icons.check}</div>
                            <div class="step-info">
                                <h4>${step.title}</h4>
                            </div>
                            <div class="step-status">
                                <span class="check" style="display:none">${icons.check}</span>
                                <span class="loading" style="display:none">${icons.loader}</span>
                            </div>
                        </div>
                    `,
                )
                .join("")}
                </div>
            </div>

            <!-- Main Content -->
            <div class="wizard-main">
                <div class="wizard-content" id="wizard-content"></div>
                <div class="wizard-footer">
                    <div class="progress-info">
                        Step <span id="current-step">1</span> of ${this.steps.length}
                    </div>
                    <div class="wizard-buttons">
                        <button class="btn btn-secondary" id="back-btn" style="display:none">Back</button>
                        <button class="btn btn-primary" id="next-btn" disabled>
                            <span id="btn-text">Continue</span>
                            <span id="btn-loader" style="display:none">${icons.loader}</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- User Panel -->
            <div class="user-panel">
                <div class="user-card">
                    <div class="user-avatar">${initials}</div>
                    <div class="user-name">${this.user_details.name}</div>
                    <div class="user-email">${this.user_details.email}</div>
                    <div class="user-details">
                        <div class="user-detail-item">
                            <span class="label">Attempt ID</span>
                            <span class="value">${this.user_details.attempt_id}</span>
                        </div>
                        <div class="user-detail-item">
                            <span class="label">Exam</span>
                            <span class="value">${this.user_details.exam_name}</span>
                        </div>
                    </div>
                </div>
                <div class="progress-bar-container">
                    <div class="progress-bar-label">
                        <span>Progress</span>
                        <span id="progress-percent">0%</span>
                    </div>
                    <div class="progress-bar">
                        <div class="progress-bar-fill" id="progress-fill" style="width: 0%"></div>
                    </div>
                </div>
            </div>
        `;

        // Event listeners
        this.overlay
            .querySelector("#back-btn")
            .addEventListener("click", () => this.goBack());
        this.overlay
            .querySelector("#next-btn")
            .addEventListener("click", () => this.goNext());

        this.shadowRoot.appendChild(this.overlay);
    }

    updateUI() {
        // Update step list
        const stepItems = this.overlay.querySelectorAll(".step-item");
        stepItems.forEach((item, i) => {
            item.classList.remove("active", "completed");
            const checkIcon = item.querySelector(".check");
            const loadingIcon = item.querySelector(".loading");

            checkIcon.style.display = "none";
            loadingIcon.style.display = "none";

            if (i < this.currentStep) {
                item.classList.add("completed");
                checkIcon.style.display = "block";
            } else if (i === this.currentStep) {
                item.classList.add("active");
                if (this.isExecuting) {
                    loadingIcon.style.display = "block";
                }
            }
        });

        // Update progress
        const progress = (this.currentStep / this.steps.length) * 100;
        this.overlay.querySelector("#progress-fill").style.width =
            `${progress}%`;
        this.overlay.querySelector("#progress-percent").textContent =
            `${Math.round(progress)}%`;
        this.overlay.querySelector("#current-step").textContent =
            this.currentStep + 1;

        // Update back button visibility
        this.overlay.querySelector("#back-btn").style.display =
            this.currentStep > 0 ? "" : "none";
    }

    async executeCurrentStep() {
        const step = this.steps[this.currentStep];
        if (!step) return;

        this.isExecuting = true;
        this.updateUI();
        this.setButtonLoading(true);

        const contentArea = this.overlay.querySelector("#wizard-content");

        try {
            if (step.action && typeof this[step.action] === "function") {
                await this[step.action](contentArea, step);
            } else {
                // Default: show step info
                this.renderStepContent(contentArea, step);
                this.enableNextButton();
            }
        } catch (error) {
            this.showError(contentArea, error.message);
        }

        this.isExecuting = false;
        this.setButtonLoading(false);
        this.updateUI();
    }

    renderStepContent(container, step, extra = "") {
        container.innerHTML = `
            <div class="content-icon">${icons[step.icon] || icons.check}</div>
            <h2 class="content-title">${step.title}</h2>
            <p class="content-description">${step.description}</p>
            ${extra}
        `;
    }

    showError(container, message) {
        let errorEl = container.querySelector(".error-message");
        if (!errorEl) {
            errorEl = document.createElement("div");
            errorEl.className = "error-message";

            // Insert after description if possible, otherwise append
            const desc = container.querySelector(".content-description");
            if (desc) {
                desc.after(errorEl);
            } else {
                container.appendChild(errorEl);
            }
        }
        errorEl.textContent = message;
        errorEl.style.display = "block"; // Ensure it's visible if it was hidden

        // Update button to retry
        this.enableNextButton("Start Again");
    }

    setButtonLoading(loading) {
        const btn = this.overlay.querySelector("#next-btn");
        const text = this.overlay.querySelector("#btn-text");
        const loader = this.overlay.querySelector("#btn-loader");

        // Only disable when loading, don't auto-enable when done
        if (loading) {
            btn.disabled = true;
        }
        loader.style.display = loading ? "inline" : "none";
        text.style.opacity = loading ? "0.5" : "1";
    }

    enableNextButton(text = "Continue") {
        const btn = this.overlay.querySelector("#next-btn");
        const btnText = this.overlay.querySelector("#btn-text");
        btn.disabled = false;
        btnText.textContent = text;
    }

    disableNextButton() {
        this.overlay.querySelector("#next-btn").disabled = true;
    }

    goBack() {
        if (this.currentStep > 0) {
            this.currentStep--;
            this.executeCurrentStep();
        }
    }

    goNext() {
        // On final step, verify streams are still active
        if (this.currentStep === this.steps.length - 1) {
            const validation = this.validateStreams();
            if (!validation.valid) {
                this.showStreamError(validation.error);
                return;
            }
        }

        this.callbacks.onStepComplete?.(
            this.steps[this.currentStep],
            this.currentStep,
        );

        if (this.currentStep < this.steps.length - 1) {
            this.currentStep++;
            this.overlay.querySelector("#btn-text").textContent = "Continue";
            this.executeCurrentStep();
        } else {
            // All steps complete
            this.complete();
        }
    }

    /**
     * Validate that camera and screen streams are still active
     */
    validateStreams() {
        // Check camera stream
        if (this.streams.camera) {
            const videoTracks = this.streams.camera.getVideoTracks();
            const audioTracks = this.streams.camera.getAudioTracks();

            const videoActive = videoTracks.some(
                (t) => t.readyState === "live",
            );
            const audioActive = audioTracks.some(
                (t) => t.readyState === "live",
            );

            if (!videoActive) {
                return {
                    valid: false,
                    error: "camera",
                    message:
                        "Camera access was stopped. Please allow camera access again.",
                };
            }
            if (!audioActive) {
                return {
                    valid: false,
                    error: "mic",
                    message:
                        "Microphone access was stopped. Please allow microphone access again.",
                };
            }
        } else {
            return {
                valid: false,
                error: "camera",
                message: "Camera access is required.",
            };
        }

        // Check screen stream
        if (this.streams.screen) {
            const screenTracks = this.streams.screen.getVideoTracks();
            const screenActive = screenTracks.some(
                (t) => t.readyState === "live",
            );

            if (!screenActive) {
                return {
                    valid: false,
                    error: "screen",
                    message:
                        "Screen sharing was stopped. Please share your screen again.",
                };
            }
        } else {
            return {
                valid: false,
                error: "screen",
                message: "Screen sharing is required.",
            };
        }

        return { valid: true };
    }

    /**
     * Show error when stream is no longer active
     */
    showStreamError(errorType) {
        const contentArea = this.overlay.querySelector("#wizard-content");

        let title, message, stepToGo;

        if (errorType === "camera" || errorType === "mic") {
            title = "Camera/Microphone Stopped";
            message =
                "Your camera or microphone was stopped. Please go back and allow access again.";
            stepToGo = this.steps.findIndex((s) => s.id === "camera");
        } else {
            title = "Screen Sharing Stopped";
            message =
                "Your screen sharing was stopped. Please share your entire screen again.";
            stepToGo = this.steps.findIndex((s) => s.id === "screen");
        }

        contentArea.innerHTML = `
            <div class="content-icon error">${icons.error}</div>
            <h2 class="content-title">${title}</h2>
            <p class="content-description">${message}</p>
        `;

        // Add button to go to the failed step
        const retryBtn = document.createElement("button");
        retryBtn.className = "btn btn-primary";
        retryBtn.innerHTML = `<span>Fix This</span>`;
        retryBtn.style.marginTop = "16px";
        retryBtn.onclick = () => {
            if (stepToGo >= 0) {
                this.currentStep = stepToGo;
                this.executeCurrentStep();
            }
        };
        contentArea.appendChild(retryBtn);

        this.disableNextButton();
    }

    async complete() {
        this.setButtonLoading(true);

        try {
            await this.callbacks.onComplete?.(this.streams);
            this.hide();
        } catch (error) {
            const contentArea = this.overlay.querySelector("#wizard-content");
            this.showError(
                contentArea,
                error.message || "An error occurred. Please try again.",
            );
            this.setButtonLoading(false);
        }
    }

    hide() {
        if (this.overlay) {
            this.overlay.remove();
            this.overlay = null;
        }
    }

    destroy() {
        // Stop any streams
        Object.values(this.streams).forEach((stream) => {
            if (stream) {
                stream.getTracks().forEach((t) => t.stop());
            }
        });
        this.streams = { camera: null, screen: null };
        this.hide();
    }

    // ========== Browser Detection Utilities ==========

    /**
     * Detects if user is on a mobile device
     */
    isMobileDevice() {
        // Check User Agent
        const userAgent =
            navigator.userAgent || navigator.vendor || window.opera;
        const mobileRegex =
            /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;

        // Check touch capability and screen size
        const isTouchDevice =
            "ontouchstart" in window || navigator.maxTouchPoints > 0;
        const isSmallScreen = window.screen.width <= 768;

        // Check platform (if available)
        let isMobilePlatform = false;
        if (navigator.userAgentData && navigator.userAgentData.platform) {
            const platform = navigator.userAgentData.platform.toLowerCase();
            isMobilePlatform = ["android", "ios"].includes(platform);
        }

        return (
            mobileRegex.test(userAgent) ||
            (isTouchDevice && isSmallScreen) ||
            isMobilePlatform
        );
    }

    /**
     * Detects browser type and version
     */
    getBrowserInfo() {
        const ua = navigator.userAgent;
        let browserName = "Unknown";
        let version = "Unknown";

        if (ua.includes("Firefox/")) {
            browserName = "Firefox";
            version = ua.match(/Firefox\/(\d+)/)?.[1] || "Unknown";
        } else if (ua.includes("Edg/")) {
            browserName = "Edge";
            version = ua.match(/Edg\/(\d+)/)?.[1] || "Unknown";
        } else if (ua.includes("Chrome/") && !ua.includes("Edg/")) {
            browserName = "Chrome";
            version = ua.match(/Chrome\/(\d+)/)?.[1] || "Unknown";
        } else if (ua.includes("Safari/") && !ua.includes("Chrome")) {
            browserName = "Safari";
            version = ua.match(/Version\/(\d+)/)?.[1] || "Unknown";
        }

        return { browserName, version };
    }

    /**
     * Check individual browser features
     */
    checkBrowserFeatures() {
        return {
            getUserMedia: !!(
                navigator.mediaDevices && navigator.mediaDevices.getUserMedia
            ),
            getDisplayMedia: !!(
                navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia
            ),
            enumerateDevices: !!(
                navigator.mediaDevices &&
                navigator.mediaDevices.enumerateDevices
            ),
            shadowDOM: !!document.createElement("div").attachShadow,
            mediaStream: !!(window.MediaStream || window.webkitMediaStream),
            webAssembly:
                typeof WebAssembly === "object" &&
                typeof WebAssembly.instantiate === "function",
            // LiveKit requirements
            webRTC: !!(
                window.RTCPeerConnection ||
                window.webkitRTCPeerConnection ||
                window.mozRTCPeerConnection
            ),
            webSocket: "WebSocket" in window,
        };
    }

    // ========== Browser Check Step ==========

    async checkBrowser(container, step) {
        // Define all checks
        const checks = [
            {
                id: "mobile",
                title: "Desktop Device",
                description: "Screen sharing requires a desktop computer",
                check: () => !this.isMobileDevice(),
                errorMessage:
                    "Mobile devices are not supported. Please use a desktop or laptop computer with Chrome, Edge, or Firefox browser.",
                blocking: true,
            },
            {
                id: "getUserMedia",
                title: "Camera & Microphone Support",
                description: "Required to access your camera and microphone",
                check: () => this.checkBrowserFeatures().getUserMedia,
                errorMessage:
                    "Your browser does not support camera access. Please use a modern browser like Chrome, Edge, or Firefox.",
                blocking: true,
            },
            {
                id: "getDisplayMedia",
                title: "Screen Sharing Support",
                description: "Required to share your screen during the exam",
                check: () => this.checkBrowserFeatures().getDisplayMedia,
                errorMessage:
                    "Your browser does not support screen sharing. Please use Chrome, Edge, or Firefox on desktop.",
                blocking: true,
            },
            {
                id: "enumerateDevices",
                title: "Device Selection",
                description: "Required to select your camera and microphone",
                check: () => this.checkBrowserFeatures().enumerateDevices,
                errorMessage:
                    "Your browser does not support device selection. Please update your browser to the latest version.",
                blocking: true,
            },
            {
                id: "webAssembly",
                title: "AI Processing (WebAssembly)",
                description: "Required for face detection and proctoring AI",
                check: () => this.checkBrowserFeatures().webAssembly,
                errorMessage:
                    "Your browser does not support WebAssembly. Please update your browser or use Chrome/Edge.",
                blocking: true,
            },
            {
                id: "webRTC",
                title: "Live Video (WebRTC)",
                description: "Required for real-time video streaming",
                check: () => this.checkBrowserFeatures().webRTC,
                errorMessage:
                    "Your browser does not support WebRTC. This is required for live proctoring. Please use Chrome, Edge, or Firefox.",
                blocking: true,
            },
        ];

        // Render initial UI with loading state
        this.renderBrowserCheckUI(container, step, checks, "checking");

        // Run checks sequentially with animation delay
        let allPassed = true;
        const results = [];

        for (let i = 0; i < checks.length; i++) {
            const checkItem = checks[i];

            // Highlight current check
            this.updateCheckStatus(container, checkItem.id, "checking");

            // Small delay for visual effect
            await new Promise((resolve) => setTimeout(resolve, 250));

            // Run the check
            const passed = checkItem.check();
            results.push({ ...checkItem, passed });

            // Update UI
            this.updateCheckStatus(
                container,
                checkItem.id,
                passed ? "pass" : "fail",
                passed ? null : checkItem.errorMessage,
            );

            if (!passed && checkItem.blocking) {
                allPassed = false;
            }
        }

        // Final state
        if (allPassed) {
            // All checks passed - update header to success
            const iconEl = container.querySelector(".content-icon");
            const titleEl = container.querySelector(".content-title");
            const descEl = container.querySelector(".content-description");

            if (iconEl) {
                iconEl.classList.add("success");
                iconEl.innerHTML = icons.check;
            }
            if (titleEl) {
                titleEl.textContent = "System Compatible";
            }
            if (descEl) {
                descEl.textContent =
                    "Your browser and device meet all requirements. Click Continue to proceed.";
            }

            this.enableNextButton();
        } else {
            // Some checks failed - show recommended browsers and block
            this.showBrowserRecommendations(container);
            this.disableNextButton();
        }
    }

    /**
     * Render browser check UI with checklist
     */
    renderBrowserCheckUI(container, step, checks, state) {
        const browserInfo = this.getBrowserInfo();

        container.innerHTML = `
            <div class="content-icon ${state === "failed" ? "error" : ""}" style="width:60px;height:60px;margin-bottom:16px;">
                ${state === "failed" ? icons.error : icons.monitor}
            </div>
            <h2 class="content-title" style="font-size:20px;margin-bottom:4px;">${state === "failed" ? "Compatibility Issues" : step.title}</h2>
            <p class="content-description" style="margin-bottom:16px;">
                ${state === "failed"
                ? "Your browser or device does not meet requirements."
                : "Detected: " +
                browserInfo.browserName +
                " " +
                browserInfo.version
            }
            </p>

            <div class="check-list">
                ${checks
                .map(
                    (check) => `
                    <div class="check-item" data-check-id="${check.id}">
                        <div class="check-icon checking">
                            ${icons.loader}
                        </div>
                        <div class="check-content">
                            <div class="check-title">${check.title}</div>
                        </div>
                    </div>
                `,
                )
                .join("")}
            </div>
        `;
    }

    /**
     * Update individual check status
     */
    updateCheckStatus(container, checkId, status, errorMessage = null) {
        const checkItem = container.querySelector(
            `[data-check-id="${checkId}"]`,
        );
        if (!checkItem) return;

        // Update item classes
        checkItem.className = `check-item ${status}`;

        // Update icon
        const iconEl = checkItem.querySelector(".check-icon");
        iconEl.className = `check-icon ${status}`;

        if (status === "checking") {
            iconEl.innerHTML = icons.loader;
        } else if (status === "pass") {
            iconEl.innerHTML = icons.check;
        } else if (status === "fail") {
            iconEl.innerHTML = icons.error;
        }

        // Add error message if failed
        if (status === "fail" && errorMessage) {
            const contentEl = checkItem.querySelector(".check-content");
            let errorEl = contentEl.querySelector(".check-error");
            if (!errorEl) {
                errorEl = document.createElement("div");
                errorEl.className = "check-error";
                contentEl.appendChild(errorEl);
            }
            errorEl.textContent = errorMessage;
        }
    }

    /**
     * Show recommended browsers section
     */
    showBrowserRecommendations(container) {
        const checkList = container.querySelector(".check-list");
        if (!checkList) return;

        // Update header
        const iconEl = container.querySelector(".content-icon");
        const titleEl = container.querySelector(".content-title");
        const descEl = container.querySelector(".content-description");

        if (iconEl) {
            iconEl.classList.add("error");
            iconEl.innerHTML = icons.error;
        }
        if (titleEl) {
            titleEl.textContent = "Compatibility Issues Detected";
        }
        if (descEl) {
            descEl.textContent =
                "Your current browser or device does not meet the requirements for proctoring.";
        }

        // Add recommendations
        const recommendationsHtml = `
            <div class="recommended-browsers">
                <h4>Recommended Browsers (Desktop Only)</h4>
                <div class="browser-list">
                    <span class="browser-badge recommended">Chrome (Latest)</span>
                    <span class="browser-badge recommended">Edge (Latest)</span>
                    <span class="browser-badge">Firefox (Latest)</span>
                    <span class="browser-badge">Safari (Limited)</span>
                </div>
            </div>
        `;

        checkList.insertAdjacentHTML("beforeend", recommendationsHtml);
    }

    // ========== Step Actions ==========

    async requestCamera(container, step) {
        // First, request initial permission to get device list
        this.renderStepContent(
            container,
            step,
            `
            <div class="permission-pending">
                ${icons.loader}
                <p>Requesting camera and microphone access...<br>Please allow when prompted.</p>
            </div>
        `,
        );

        try {
            // Initial permission request to unlock device enumeration - 1080p for live view
            const initialStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    frameRate: { ideal: 15, max: 15 },
                    codec: "h264",
                },
                audio: true,
            });

            // Stop initial stream - we'll create a new one with selected devices
            initialStream.getTracks().forEach((t) => t.stop());

            // Get available devices
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter((d) => d.kind === "videoinput");
            const audioDevices = devices.filter((d) => d.kind === "audioinput");

            // Render device selection UI - side by side layout
            container.innerHTML = `
                <div class="content-icon">${icons.camera}</div>
                <h2 class="content-title">${step.title}</h2>
                <p class="content-description">${step.description}</p>
                <div class="camera-setup">
                    <div class="camera-preview-container">
                        <video id="camera-preview" autoplay playsinline muted></video>
                    </div>
                    <div class="device-selectors">
                        <div class="device-select-group">
                            <label class="device-select-label">
                                ${icons.camera}
                                <span>Camera</span>
                            </label>
                            <select id="camera-select" class="device-select">
                                ${videoDevices
                    .map(
                        (d, i) => `
                                    <option value="${d.deviceId}">${d.label || `Camera ${i + 1}`}</option>
                                `,
                    )
                    .join("")}
                            </select>
                        </div>
                        <div class="device-select-group">
                            <label class="device-select-label">
                                ${icons.mic}
                                <span>Microphone</span>
                            </label>
                            <select id="mic-select" class="device-select">
                                ${audioDevices
                    .map(
                        (d, i) => `
                                    <option value="${d.deviceId}">${d.label || `Microphone ${i + 1}`}</option>
                                `,
                    )
                    .join("")}
                            </select>
                        </div>
                    </div>
                </div>
            `;

            const cameraSelect = container.querySelector("#camera-select");
            const micSelect = container.querySelector("#mic-select");
            const video = container.querySelector("#camera-preview");

            // Function to update stream with selected devices
            const updateStream = async () => {
                // Stop existing stream
                if (this.streams.camera) {
                    this.streams.camera.getTracks().forEach((t) => t.stop());
                }

                try {
                    this.streams.camera =
                        await navigator.mediaDevices.getUserMedia({
                            video: {
                                deviceId: { exact: cameraSelect.value },
                                width: { ideal: 1280 },
                                height: { ideal: 720 },
                                frameRate: { ideal: 15, max: 15 },
                                codec: "h264",
                            },
                            audio: {
                                deviceId: { exact: micSelect.value },
                            },
                        });

                    video.srcObject = this.streams.camera;
                    this.enableNextButton();
                } catch (err) {
                    this.showError(
                        container,
                        `Failed to access device: ${err.message}`,
                    );
                    this.disableNextButton();
                }
            };

            // Listen for device changes
            cameraSelect.addEventListener("change", updateStream);
            micSelect.addEventListener("change", updateStream);

            // Initial stream with default devices
            await updateStream();
        } catch (err) {
            // Permission denied
            this.renderStepContent(
                container,
                step,
                `
                <div class="content-icon error">${icons.error}</div>
                <h2 class="content-title">Permission Denied</h2>
                <p class="content-description">
                    Camera and microphone access is required to continue.
                    Please allow access in your browser settings and try again.
                </p>
            `,
            );
            this.disableNextButton();
            this.overlay.querySelector("#btn-text").textContent = "Try Again";
            this.overlay.querySelector("#next-btn").disabled = false;
            this.overlay.querySelector("#next-btn").onclick = () => {
                this.overlay.querySelector("#next-btn").onclick = null;
                this.executeCurrentStep();
            };
            throw new Error(`Camera/Mic access denied: ${err.message}`);
        }
    }

    async requestScreen(container, step) {
        this.renderStepContent(
            container,
            step,
            `
            <div class="permission-pending">
                ${icons.screen}
                <p>Click the button below to share your screen.<br>Select "Entire Screen" when prompted.</p>
            </div>
        `,
        );

        // Disable next until screen is shared
        this.disableNextButton();

        // Create a share button inside content
        const shareBtn = document.createElement("button");
        shareBtn.className = "btn btn-primary";
        shareBtn.innerHTML = `${icons.screen} <span>Share Screen</span>`;
        shareBtn.style.marginTop = "16px";
        container.querySelector(".permission-pending").appendChild(shareBtn);

        shareBtn.onclick = async () => {
            shareBtn.disabled = true;
            shareBtn.innerHTML = `${icons.loader} <span>Requesting...</span>`;

            try {
                const stream = await navigator.mediaDevices.getDisplayMedia({
                    video: {
                        displaySurface: "monitor",
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                        frameRate: { ideal: 10, max: 10 },
                        codec: "h264",
                    },
                    preferCurrentTab: false,
                    selfBrowserSurface: "exclude",
                    surfaceSwitching: "exclude",
                    monitorTypeSurfaces: "include",
                });

                // Check if user selected entire screen (monitor) vs window/tab
                const videoTrack = stream.getVideoTracks()[0];
                const settings = videoTrack.getSettings();
                const displaySurface = settings.displaySurface;

                if (displaySurface !== "monitor") {
                    // User selected window or tab - reject it
                    stream.getTracks().forEach((t) => t.stop());

                    this.renderStepContent(
                        container,
                        step,
                        `
                        <div class="content-icon error">${icons.error}</div>
                        <h2 class="content-title">Entire Screen Required</h2>
                        <p class="content-description">
                            You selected a ${displaySurface === "window" ? "window" : "browser tab"}.
                            You must share your <strong>Entire Screen</strong> to continue.
                            Please try again and select "Entire Screen" option.
                        </p>
                    `,
                    );

                    // Add retry button
                    const retryBtn = document.createElement("button");
                    retryBtn.className = "btn btn-primary";
                    retryBtn.innerHTML = `${icons.screen} <span>Try Again</span>`;
                    retryBtn.style.marginTop = "16px";
                    retryBtn.onclick = () => this.executeCurrentStep();
                    container
                        .querySelector(".content-description")
                        .after(retryBtn);

                    this.disableNextButton();
                    return;
                }

                // Valid entire screen share
                this.streams.screen = stream;

                // Show success
                this.renderStepContent(
                    container,
                    step,
                    `
                    <div class="content-icon success">${icons.check}</div>
                    <h2 class="content-title">Screen Sharing Active</h2>
                    <p class="content-description">Your entire screen is now being shared. Click Continue to proceed.</p>
                `,
                );

                // Enable continue button
                this.enableNextButton("Continue");
            } catch (err) {
                // Show error with retry
                this.renderStepContent(
                    container,
                    step,
                    `
                    <div class="content-icon error">${icons.error}</div>
                    <h2 class="content-title">Screen Share Required</h2>
                    <p class="content-description">
                        You must share your entire screen to continue.
                        Please select "Entire Screen" when prompted.
                    </p>
                `,
                );

                // Add retry button
                const retryBtn = document.createElement("button");
                retryBtn.className = "btn btn-primary";
                retryBtn.innerHTML = `${icons.screen} <span>Try Again</span>`;
                retryBtn.style.marginTop = "16px";
                retryBtn.onclick = () => this.executeCurrentStep();
                container.querySelector(".content-description").after(retryBtn);

                // Keep next disabled
                this.disableNextButton();
            }
        };
    }

    async showInstructions(container, step) {
        // Define instructions with short summaries and detailed expansions
        const instructions = [
            {
                title: "AI Monitoring Active",
                summary: "Your session is recorded and analyzed for fairness.",
                detail: "Our AI system continuously monitors your video and audio feed. Any unusual activity is flagged for review by your proctor or institution.",
                icon: icons.monitor
            },
            {
                title: "Keep Face Visible",
                summary: "Don't look away or cover your face.",
                detail: "Ensure your entire face is clearly visible within the camera frame at all times. Do not wear masks, sunglasses, or hats that obscure your identity.",
                icon: icons.user
            },
            {
                title: "No Distractions",
                summary: "You must be alone in a quiet room.",
                detail: "Ensure you are in a private, well-lit environment. No other people, electronic devices (phones, tablets), or study materials are allowed in the room.",
                icon: icons.shield
            },
            {
                title: "Strict Policy",
                summary: "Suspicious activity will be flagged instantly.",
                detail: "Any attempt to leave the browser window, use other applications, or communicate with others will be recorded as a potential violation.",
                icon: icons.alert
            }
        ];

        const chevronIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>`;

        // Render instructions UI
        container.innerHTML = `
            <div class="content-icon">${icons.shield}</div>
            <h2 class="content-title">${step.title}</h2>
            <p class="content-description">${step.description}</p>

            <div class="instructions-list">
                ${instructions.map((item, index) => `
                    <div class="instruction-item" data-index="${index}">
                        <button class="instruction-header">
                            <div class="instruction-icon">
                                ${item.icon}
                            </div>
                            <div class="instruction-content">
                                <h5>${item.title}</h5>
                                <p>${item.summary}</p>
                            </div>
                            <div class="instruction-toggle">
                                ${chevronIcon}
                            </div>
                        </button>
                        <div class="instruction-details">
                            ${item.detail}
                        </div>
                    </div>
                `).join('')}
            </div>

            <div class="acknowledgement-box">
                <input type="checkbox" id="acknowledge-check">
                <label for="acknowledge-check">
                    I have read and understand the proctoring guidelines.
                </label>
            </div>
        `;

        // Handle Expand/Collapse
        const items = container.querySelectorAll('.instruction-item');
        items.forEach(item => {
            const header = item.querySelector('.instruction-header');
            header.addEventListener('click', () => {
                // Close others
                items.forEach(other => {
                    if (other !== item) other.classList.remove('expanded');
                });
                // Toggle current
                item.classList.toggle('expanded');
            });
        });

        // Handle acknowledgement
        const checkbox = container.querySelector('#acknowledge-check');
        const ackBox = container.querySelector('.acknowledgement-box');

        // Make the whole box clickable
        ackBox.addEventListener('click', (e) => {
            if (e.target !== checkbox) {
                checkbox.checked = !checkbox.checked;
                // Manually trigger change event
                checkbox.dispatchEvent(new Event('change'));
            }
        });

        this.disableNextButton(); // Disable until checked

        checkbox.addEventListener('change', (e) => {
            if (e.target.checked) {
                this.enableNextButton("Start Exam");
                ackBox.style.borderColor = "var(--wizard-primary)";
                ackBox.style.background = "rgba(59, 130, 246, 0.1)";
            } else {
                this.disableNextButton();
                ackBox.style.borderColor = "";
                ackBox.style.background = "";
            }
        });
    }
}

export default new PermissionWizard();
